import React, { useContext, useEffect, useRef, useState } from "react";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { Box, Flex, Grid, Link, Spinner, Label } from "theme-ui";
import { useNavigate, useParams } from "react-router-dom";
import ReloadIcon from "components/svg/icons/ReloadIcon";
import { deleteService, getService, updateService, resyncService } from "api/Services";
import { getServiceAvailability, setServiceAvailability } from "api/Services";
import { getClients } from "api/Clients";
import { trackActivity } from "api/ActivityTracker";

import { filterByKeys } from "helpers/default";

import Header from "components/ui/Header";
import Page from "components/ui/Page";
import { Tab, Tabs } from "components/ui/Tabs";
import BasicButton from "components/forms/BasicButton";
import { useToast } from "components/toast";
import { BackArrowIcon, CheckMarkIcon, QuestionMarkIcon, TrashIcon, PlusIcon, ClientsIcon } from "components/svg";
import SearchField from "components/forms/SearchField";
import Checkbox from "components/forms/Checkbox";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "components/ui/Modal";
import { ModalIcon, ModalSubtitle, ModalTitle, ModalTitleWrapper } from "components/ui/Header/styled";
import { Badge } from "theme-ui";
import H5 from "components/typography/H5";
import Subtext from "components/typography/Subtext";
import TextInput from "components/forms/TextInput";
import { themevals } from "theme/themevals";
import BasicModal from "components/ui/BasicModal";
import P from "components/typography/P";
import Separator from "components/ui/Separator";
import UserContext from "context/User";


export default function Service() {
  const navigate = useNavigate();
  const params = useParams();
  const toast = useToast();  

  const [service,setService] = useState<any>(null);
  const { user } = useContext<any>(UserContext);
  const hasTrackedView = useRef(false);

  // --- Client availability (Fibar install-only project) ---
  const [availability, setAvailability] = useState<any[]>([]); // rows with .client
  const [availLoading, setAvailLoading] = useState<boolean>(false);
  const [availModalOpen, setAvailModalOpen] = useState<boolean>(false);
  const [companyClients, setCompanyClients] = useState<any[]>([]);
  const [availSearch, setAvailSearch] = useState<string>("");
  const [availSaving, setAvailSaving] = useState<boolean>(false);

  const initAvailability = async () => {
    setAvailLoading(true);
    const res = await getServiceAvailability({ data: { id: params.id } });
    setAvailability(Array.isArray(res) ? res : []);
    setAvailLoading(false);
  };

  const loadCompanyClients = async () => {
    // Company/org clients (type 2) are the ones availability keys on.
    const res = await getClients({ query: { filters: { type: "2:is" }, recordsPer: 1000 } });
    setCompanyClients(res?.items ?? []);
  };

  const isRestricted = availability.length > 0;

  // Replace the whole allowlist. Pass the full desired set of client ids.
  const persistAvailability = async (clientIds: string[]) => {
    setAvailSaving(true);
    const res = await setServiceAvailability({ data: { id: params.id }, clientIds });
    if (Array.isArray(res)) {
      setAvailability(res);
      toast.add("Client availability updated.", "var(--theme-ui-colors-success)");
      void trackActivity({
        module: "services",
        action: "update_service_availability",
        actionType: "update",
        userId: user?.id || null,
        companyId: user?.company?.id || null,
        entityType: "service",
        entityId: params.id ?? null,
        metadata: { clientCount: clientIds.length },
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
      });
    } else {
      toast.add(res?.message ? res.message : "Error updating availability.", "var(--theme-ui-colors-danger)");
    }
    setAvailSaving(false);
  };

  const removeAvailabilityClient = async (clientId: string) => {
    const remaining = availability.map((r) => r.clientId).filter((id) => id !== clientId);
    await persistAvailability(remaining);
  };

  const toggleAvailModal = () => {
    if (!availModalOpen) {
      setAvailSearch("");
      if (companyClients.length === 0) loadCompanyClients();
    }
    setAvailModalOpen(!availModalOpen);
  };

  const submitUpdate = async (values: any) => {   
    const keys = ["name","description"];    
    const update = filterByKeys(keys,values);
    
    const previousService = service;
    const res = await updateService({ data : { id : params.id }, update });
    if(res?.id){ 
      toast.add("Service updated.","var(--theme-ui-colors-success)");
      setService(res);

      if (previousService?.id) {
        const changedFields: string[] = [];
        if ((previousService.name ?? "") !== (res.name ?? "")) {
          changedFields.push("name");
        }
        if ((previousService.description ?? "") !== (res.description ?? "")) {
          changedFields.push("description");
        }

        if (changedFields.length > 0) {
          void trackActivity({
            module: "services",
            action: "update_service",
            actionType: "update",
            userId: user?.id || null,
            companyId: user?.company?.id || null,
            entityType: "service",
            entityId: res.id,
            metadata: {
              changedFields,
            },
            userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
          });
        }
      }
    }else{
      toast.add(res?.message ? res.message : "Error updating service","var(--theme-ui-colors-danger)");
    }                 
  };  

  const _validate = Yup.object().shape({
    name: Yup.string().required('This field is required.'),
  });

  const initService= async () => {
    const res = await getService({ data : {id : params.id }});
    setService(res?.id ? res : null);
    initAvailability();
  }

  const [confirmModal, setConfirmModal] = useState<any>(false);
  const [confirmSubmit, setConfirmSubmit] = useState<boolean>(false);

  const confirmDelete = async () => { 
    setConfirmSubmit(true);   
    const res = await deleteService({ data: service });
    if(res?.success){ 
      toast.add("Service deactivated.","var(--theme-ui-colors-success)");  
      void trackActivity({
        module: "services",
        action: "deactivate_service",
        actionType: "update",
        userId: user?.id || null,
        companyId: user?.company?.id || null,
        entityType: "service",
        entityId: service?.id ?? null,
        metadata: {
          source: "service_detail",
        },
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
      });
      navigate("/services")
    }else{
      toast.add(res?.message ? res.message : "Error deleting client.","var(--theme-ui-colors-danger)");
    } 
    setConfirmSubmit(false);   
    setConfirmModal(false)          
  };
  
  const formatLastSync = (iso?: string) => {
    if (!iso) return "N/A";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "N/A";
    const month = d.toLocaleString("en-US", { month: "short" });
    const day = d.getDate();
    const year = d.getFullYear();
    const ordinal = (n: number) => {
      const s = ["th", "st", "nd", "rd"];
      const v = n % 100;
      return s[(v - 20) % 10] || s[v] || s[0];
    };
    let hours = d.getHours();
    const minutes = d.getMinutes().toString().padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12;
    return `${month} ${day}${ordinal(day)}, ${year} ${hours}:${minutes} ${ampm}`;
  };

   const submitSync = async () => {
      setConfirmSubmit(true);
      const res = await resyncService({ data: service });
      if (res?.success) {
        await initService();
        toast.add("Service synced.", "var(--theme-ui-colors-success)");
      } else {
        toast.add(res?.message ? res.message : "Error syncing service.", "var(--theme-ui-colors-danger)");
      }
      setConfirmSubmit(false);
    };
  
    const lastSyncSuccessful = service?.external?.zoho?.lastSyncSuccessful;
    const lastSyncColor =
      lastSyncSuccessful === undefined
        ? themevals.colors.dkgrey
        : lastSyncSuccessful
        ? themevals.colors.success
        : themevals.colors.danger;
    const lastSyncText = service?.external?.zoho?.lastSync
      ? formatLastSync(service.external.zoho.lastSync)
      : "N/A";

  const submitDelete = async () => {
    setConfirmModal(<BasicModal
      title="Deactivate service?"       
      size={"sm"}
      icon={<QuestionMarkIcon fill={"#818181"} height="30px"/>}
      cancel_click={() => setConfirmModal(false)}
      cancel_text="No, cancel"
      actions={<Flex sx={{gap: "10px"}}>
        <BasicButton
          type="button"    
          scheme="danger"      
          onClick={() => confirmDelete()} 
          $submitting={confirmSubmit}
          $iconHeight="14px"
          $iconWidth="14px"
          $loadingColor="#fff"
          keepText={true}
        >
          {!confirmSubmit && <TrashIcon fill="#fff"/>}        
          {"Deactivate"}
        </BasicButton>
      </Flex>}    
    >
      <P lineHeight={"22px"}>Are you sure you want to deactivate <strong style={{color:"var(--theme-ui-colors-black)"}}>{service?.name}</strong>?</P>
    </BasicModal>);
  }

  useEffect(() => {    
    document.title = process.env.REACT_APP_NAME + " | Service"
    initService(); 
    hasTrackedView.current = false;
  }, []); 

  useEffect(() => {
    if (!service?.id || hasTrackedView.current) {
      return;
    }
    hasTrackedView.current = true;
    void trackActivity({
      module: "services",
      action: "view_service_detail",
      actionType: "view",
      userId: user?.id || null,
      companyId: user?.company?.id || null,
      entityType: "service",
      entityId: service.id,
      metadata: null,
      userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
    });
  }, [service?.id, user?.id, user?.company?.id]);

  if(!service) return <Box sx={{textAlign: "center", paddingY: "1.5rem"}}><Spinner /></Box>


  return (
    <>
      <Header></Header>      
      <Page 
        title="Edit service" 
        subtitle="Edit a service of your company." 
        backAction={<Link variant="back" href="/services"><BackArrowIcon />Back to Services</Link>}
      >                
        <Formik initialValues={{...service}} onSubmit={submitUpdate} validationSchema={_validate} enableReinitialize>
          {({ isSubmitting, errors, values, submitCount, setFieldValue }) => (
            <Form noValidate autoComplete="off">   
              <Tabs
                vertical
                stickyFooter
                footer={<Flex sx={{gap: "10px", flexGrow : 1}}>
                  <BasicButton 
                    scheme="dangerSecondary"
                    styles={{marginRight:"auto!important", marginLeft:"10px"}}
                    type="button" 
                    onClick={() => submitDelete()}                                        
                  >
                    <TrashIcon fill={themevals.colors.danger}/> Deactivate
                  </BasicButton>                  
                  <Link href="/services" sx={{...themevals.buttons.secondaryOutline, textDecoration: "none", margin:"0 10px 0 0"}}>Cancel</Link>
                  <BasicButton 
                    type="submit" 
                    $submitting={isSubmitting}
                    styles={{margin:"0 10px 0 0"}}
                    $iconHeight="14px"
                    $iconWidth="14px"
                    $loadingColor="#fff"
                    keepText={true}
                  >
                    {!isSubmitting && <CheckMarkIcon fill={"#fff"}/>}
                    Save service
                  </BasicButton>
                </Flex>}
              >
                <Tab itemKey="general">
                  <Grid gap={6} columns={[1, '1fr 3fr']} sx={{paddingLeft:"30px", paddingRight:"30px"}}>
                    <Box>
                      <H5 fontFamily={"Poppins"} fontWeight={"600"} color={"#000"} marginTop={"0px"} marginBottom={"5px"}>Basic Info</H5>
                      <Subtext marginTop={0} marginBottom={0} fontSize={"12px"} fontWeight={"400"} fontFamily={"Poppins"}>Edit main service information.</Subtext>
                    </Box> 
                    <Box>
                      <Flex sx={{flexDirection : ["column","row"], gap: "1rem", marginBottom : "1rem"}}>    
                        <TextInput                        
                          name="name"
                          label="Name"
                          value={values.name}                        
                          onChange={(val: any) => setFieldValue("name",val)}
                          $errors={errors.name && submitCount > 0 ? errors.name : null}    
                          $customStyles={{flexGrow: 1, width: "100%"}}
                        />                                               
                        <TextInput                        
                          name="description"
                          label="Description"
                          value={values.description}                        
                          onChange={(val: any) => setFieldValue("description",val)}
                          $errors={errors?.description && submitCount > 0 ? errors?.description : null}   
                          $customStyles={{flexGrow: 1, width: "100%"}} 
                        />
                      </Flex>                         
                    </Box>     
                  </Grid> 
                  <Separator />
                  <Grid gap={6} columns={[1, "1fr 3fr"]} sx={{ paddingLeft: "30px", paddingRight: "30px" }}>
                    <Box>
                      <H5 fontFamily={"Poppins"} fontWeight={"600"} color={"#000"} marginTop={"0px"} marginBottom={"5px"}>
                        Client Availability
                      </H5>
                      <Subtext marginTop={0} marginBottom={0} fontSize={"12px"} fontWeight={"400"} fontFamily={"Poppins"}>
                        Control which clients can use this service. Leave empty to make it available to all clients.
                      </Subtext>
                    </Box>
                    <Box>
                      <Flex sx={{ alignItems: "center", gap: "12px", marginBottom: "14px" }}>
                        {isRestricted ? (
                          <Badge variant="caution">Restricted</Badge>
                        ) : (
                          <Badge variant="ltgrey">All clients</Badge>
                        )}
                        <Subtext marginTop={0} marginBottom={0} fontSize={"12px"} fontFamily={"Poppins"}>
                          {isRestricted
                            ? "Only the clients below can select this service."
                            : "This service is available to every client."}
                        </Subtext>
                      </Flex>

                      {availLoading ? (
                        <Spinner size={20} strokeWidth={3} title="loading" />
                      ) : (
                        <>
                          {isRestricted && (
                            <Flex sx={{ flexWrap: "wrap", gap: "8px", marginBottom: "14px" }}>
                              {availability.map((row: any) => (
                                <Flex
                                  key={row.id}
                                  sx={{
                                    alignItems: "center",
                                    gap: "8px",
                                    background: themevals.colors.base_100,
                                    border: `1px solid ${themevals.colors.base_300}`,
                                    borderRadius: "6px",
                                    padding: "6px 10px 6px 12px",
                                    fontFamily: "Poppins",
                                    fontSize: "13px",
                                  }}
                                >
                                  <span>{row?.client?.name ?? "Unknown client"}</span>
                                  <Box
                                    sx={{ cursor: "pointer", color: themevals.colors.dkgrey, lineHeight: 1, fontWeight: 600 }}
                                    onClick={() => !availSaving && removeAvailabilityClient(row.clientId)}
                                  >
                                    &times;
                                  </Box>
                                </Flex>
                              ))}
                            </Flex>
                          )}

                          <BasicButton
                            type="button"
                            $iconHeight="10px"
                            scheme="secondaryOutline"
                            onClick={() => toggleAvailModal()}
                          >
                            <PlusIcon fill="#000" />
                            Add client
                          </BasicButton>

                          <Box
                            sx={{
                              marginTop: "22px",
                              padding: "14px 16px",
                              background: themevals.colors.base_100,
                              border: `1px solid ${themevals.colors.base_300}`,
                              borderRadius: "6px",
                            }}
                          >
                            <P fontFamily="Poppins" fontSize="12px" color={themevals.colors.dkgrey} marginTop="0px" marginBottom="0px">
                              <strong style={{ color: themevals.colors.black }}>How this works:</strong> With no clients listed, this
                              service behaves normally and is available to everyone. Add one or more clients and it becomes hidden from all
                              others - it will not appear in their Instant Estimate or be selectable on their RFQs.
                            </P>
                          </Box>
                        </>
                      )}
                    </Box>
                  </Grid>
                  <Separator />
                  <Grid gap={6} columns={[1, "1fr 3fr"]} sx={{ paddingLeft: "30px", paddingRight: "30px" }}>
                    <Box>
                      <H5 fontFamily={"Poppins"} fontWeight={"600"} color={"#000"} marginTop={"0px"} marginBottom={"5px"}>
                        Third-Party Sync
                      </H5>
                      <Subtext marginTop={0} marginBottom={0} fontSize={"12px"} fontWeight={"400"} fontFamily={"Poppins"}>
                        Resync your service with your CRM provider.
                      </Subtext>
                    </Box>
                    <Box>
                      <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
                        <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Last Sync</Label>
                        <P fontFamily={"Poppins"} color={lastSyncColor} fontSize={"14px"} fontWeight={600} marginTop={"5px"} marginBottom={"0px"}>
                          {lastSyncText}
                        </P>
                      </Flex>
                      <BasicButton
                        scheme="secondaryOutline"
                        type="button"
                        $iconHeight="14px"
                        $iconWidth="14px"
                        $loadingColor="#000"
                        $submitting={confirmSubmit}
                        keepText={true}
                        onClick={() => submitSync()}
                      >
                        {!confirmSubmit && <ReloadIcon fill={"#000"} />}Resync
                      </BasicButton>
                    </Box>
                  </Grid>                                                  
                </Tab>                   
              </Tabs>                                        
            </Form>
            )
          }
        </Formik>
      </Page>
      {confirmModal}

      <Modal isOpen={availModalOpen} toggle={() => toggleAvailModal()} centered size={"sm"}>
        <Formik
          initialValues={{ selectedClients: availability.map((r: any) => r.clientId) }}
          onSubmit={async (vals) => {
            await persistAvailability(vals.selectedClients);
            setAvailModalOpen(false);
          }}
          enableReinitialize
        >
          {({ isSubmitting, values, setFieldValue }: any) => {
            const toggleClient = (clientId: string) => {
              const next = values.selectedClients.includes(clientId)
                ? values.selectedClients.filter((id: string) => id !== clientId)
                : [...values.selectedClients, clientId];
              setFieldValue("selectedClients", next);
            };

            const filtered = companyClients.filter((c: any) =>
              (c?.name ?? "").toLowerCase().includes(availSearch.toLowerCase())
            );

            return (
              <Form noValidate autoComplete="off">
                <ModalHeader toggle={() => toggleAvailModal()}>
                  <ModalIcon>
                    <ClientsIcon fill={"000"} />
                  </ModalIcon>
                  <ModalTitleWrapper>
                    <ModalTitle>Client availability</ModalTitle>
                    <ModalSubtitle>{"Select the clients allowed to use this service. None selected means all clients."}</ModalSubtitle>
                  </ModalTitleWrapper>
                </ModalHeader>
                <ModalBody>
                  <SearchField value={availSearch} onChange={(e: any) => setAvailSearch(e.target.value)} width="100%" />
                  <Box sx={{ marginTop: "16px" }}>
                    {filtered.length === 0 ? (
                      <P fontFamily="Poppins" fontSize="14px" color={themevals.colors.dkgrey}>
                        No company clients found.
                      </P>
                    ) : (
                      filtered.map((c: any) => (
                        <Flex
                          key={c.id}
                          sx={{
                            gap: "1rem",
                            alignItems: "center",
                            marginBottom: "10px",
                            border: `1px solid ${values.selectedClients.includes(c.id) ? "black" : themevals.colors.mdgrey}`,
                            borderRadius: "6px",
                            height: "38px",
                            padding: "0px 10px",
                          }}
                        >
                          <Checkbox
                            label={c.name}
                            checked={values.selectedClients.includes(c.id)}
                            onChange={() => toggleClient(c.id)}
                            labelSize="14px"
                          />
                        </Flex>
                      ))
                    )}
                  </Box>
                </ModalBody>
                <ModalFooter>
                  <BasicButton scheme="secondaryOutline" type="button" onClick={() => setAvailModalOpen(false)} styles={{ marginRight: "10px" }}>
                    Cancel
                  </BasicButton>
                  <BasicButton type="submit" $submitting={isSubmitting || availSaving} $iconHeight="14px" $iconWidth="14px" $loadingColor="#fff" keepText={true}>
                    {!(isSubmitting || availSaving) && <CheckMarkIcon fill={"#fff"} />}
                    Save
                  </BasicButton>
                </ModalFooter>
              </Form>
            );
          }}
        </Formik>
      </Modal>
    </>
  );
}
